/**
 * PostLoginBillingModal
 *
 * Forces managers to select a billing model when the firm has deferred/none status.
 * When showTermsCheckbox=true, T&C acceptance is combined into the same step.
 *
 * @viewport both
 * Mobile: bottom sheet — slides up, RADIUS.lg top corners, max 90dvh
 * Desktop: centred dialog, max 500px wide, RADIUS.md corners
 */

// ── 1. React core ─────────────────────────────────────────────────────────────
import React, { useState } from 'react';

// ── 2. Third-party ────────────────────────────────────────────────────────────
import PropTypes from 'prop-types';

// ── 3. MUI components ─────────────────────────────────────────────────────────
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';

// ── 4. Internal — platform tokens ─────────────────────────────────────────────
import { TOUCH, ZINDEX, FONT_WEIGHT, RADIUS, SHADOWS, TRANSITIONS } from '@/core/platform/platformStandards';

const PostLoginBillingModal = ({
  open,
  deferredUntil,
  canDefer,
  onSelectRevShare,
  onSelectDefer,
  onSelectSubscription,
  onCancel,
  showTermsCheckbox = false,
  onOpenTermsView,
  onOpenRevShareView,
  termsAccepted: termsAcceptedProp,
  onTermsChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedModel, setSelectedModel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [termsAcceptedInternal, setTermsAcceptedInternal] = useState(false);

  const termsAccepted = showTermsCheckbox
    ? (termsAcceptedProp !== undefined ? termsAcceptedProp : termsAcceptedInternal)
    : true;

  const handleTermsCheck = (e) => {
    if (!showTermsCheckbox) return;
    if (onTermsChange) onTermsChange(e.target.checked);
    else setTermsAcceptedInternal(e.target.checked);
  };

  const handleSubmit = async () => {
    if (!selectedModel) { setError('Please select a participation option.'); return; }
    if (showTermsCheckbox && !termsAccepted) { setError('You must accept the Terms and Conditions to continue.'); return; }

    const agreementData = showTermsCheckbox && termsAccepted
      ? { agreement_accepted: true, agreement_accepted_at: new Date().toISOString(), agreement_version: '1.0' }
      : null;

    try {
      setSubmitting(true);
      setError(null);
      if (selectedModel === 'rev_share') { onSelectRevShare?.(agreementData); return; }
      if (selectedModel === 'deferred') { await onSelectDefer?.(agreementData); return; }
      if (selectedModel === 'subscription') { await onSelectSubscription?.('annual', agreementData); return; }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to select participation model. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasExistingDeferral = Boolean(deferredUntil);
  const isTrialExpired = Boolean(deferredUntil && new Date(deferredUntil) < new Date());
  const subscriptionEligible = Boolean(onSelectSubscription);
  const canContinue = selectedModel && (!showTermsCheckbox || termsAccepted);

  const options = [
    {
      value: 'rev_share',
      label: 'Revenue Share',
      description: 'No upfront cost. Fees apply only when an investment order is successfully fulfilled.',
      icon: <AccountBalanceIcon sx={{ color: 'primary.main' }} />,
      show: true,
    },
    {
      value: 'subscription',
      label: 'Subscribe',
      description: 'Pay an upfront annual fee of $10,800 for unlimited access. Complete payment via Stripe Checkout after e-signing the subscription agreement.',
      icon: <PaymentIcon sx={{ color: 'primary.main' }} />,
      show: subscriptionEligible,
    },
    {
      value: 'deferred',
      label: 'Defer Decision',
      description: 'Keep participation as Deferred. You can select revenue share later in Settings.',
      icon: <AccessTimeIcon sx={{ color: 'primary.main' }} />,
      show: canDefer,
    },
  ];

  return (
    <Dialog
      open={open}
      fullScreen={false}
      disableEscapeKeyDown={!canDefer}
      aria-labelledby="billing-dialog-title"
      sx={{ zIndex: ZINDEX.modal }}
      PaperProps={{
        sx: isMobile
          ? {
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              m: 0,
              width: '100%',
              borderRadius: `${RADIUS.lg}px ${RADIUS.lg}px 0 0`,
              maxHeight: '90dvh',
            }
          : {
              borderRadius: `${RADIUS.md}px`,
              maxWidth: 500,
            },
      }}
    >
      <DialogTitle
        id="billing-dialog-title"
        sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="h6" sx={{ fontWeight: FONT_WEIGHT.semibold }}>
          Participation Model Selection
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1 }}>
        {showTermsCheckbox && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              <Typography variant="body2">
                Please review and accept our Terms &amp; Conditions to continue. This is a one-time step.
              </Typography>
            </Alert>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={handleTermsCheck}
                  disabled={submitting}
                  sx={{ minWidth: TOUCH.tapTarget, minHeight: TOUCH.tapTarget }}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  {onOpenTermsView ? (
                    <Button
                      variant="text"
                      size="small"
                      onClick={onOpenTermsView}
                      disabled={submitting}
                      sx={{ p: 0, minWidth: 'auto', textDecoration: 'underline', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Terms and Conditions
                    </Button>
                  ) : (
                    <Typography component="span" variant="body2">Terms and Conditions</Typography>
                  )}
                </Typography>
              }
            />
          </Box>
        )}

        {hasExistingDeferral && canDefer && !isTrialExpired && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Participation is Deferred. Select revenue share when you are ready.
          </Alert>
        )}

        {isTrialExpired && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Participation is Deferred. Select revenue share when you are ready.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <RadioGroup value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
          <Stack spacing={1.5}>
            {options.filter((o) => o.show).map((opt) => (
              <Card
                key={opt.value}
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  borderColor: selectedModel === opt.value ? 'primary.main' : 'divider',
                  transition: `border-color ${TRANSITIONS.duration.shorter}ms ${TRANSITIONS.easing.easeInOut}`,
                  '&:hover': { borderColor: 'primary.main', boxShadow: SHADOWS.card },
                }}
              >
                <CardActionArea
                  onClick={() => setSelectedModel(opt.value)}
                  sx={{ p: { xs: 1.5, md: 2 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Radio
                      value={opt.value}
                      checked={selectedModel === opt.value}
                      sx={{ p: 0, minWidth: TOUCH.tapTarget, minHeight: TOUCH.tapTarget }}
                      tabIndex={-1}
                    />
                    {opt.icon}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: FONT_WEIGHT.semibold }}>
                        {opt.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="span">
                        {opt.description}
                        {opt.value === 'rev_share' && onOpenRevShareView && (
                          <>
                            {' '}
                            Review the{' '}
                            <Button
                              variant="text"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenRevShareView();
                              }}
                              disabled={submitting}
                              sx={{
                                p: 0,
                                minWidth: 'auto',
                                verticalAlign: 'baseline',
                                textDecoration: 'underline',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              Rev-Share
                            </Button>
                            {' '}
                            agreement before continuing.
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        </RadioGroup>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={submitting}
          sx={{ minHeight: TOUCH.elementHeight }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canContinue || submitting}
          sx={{ minHeight: TOUCH.elementHeight }}
        >
          {submitting ? <CircularProgress size={TOUCH.iconSize} /> : 'Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PostLoginBillingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  deferredUntil: PropTypes.string,
  canDefer: PropTypes.bool,
  onSelectRevShare: PropTypes.func,
  onSelectDefer: PropTypes.func,
  onSelectSubscription: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  showTermsCheckbox: PropTypes.bool,
  onOpenTermsView: PropTypes.func,
  onOpenRevShareView: PropTypes.func,
  termsAccepted: PropTypes.bool,
  onTermsChange: PropTypes.func,
};

PostLoginBillingModal.defaultProps = {
  deferredUntil: null,
  canDefer: true,
  showTermsCheckbox: false,
  onSelectSubscription: undefined,
};

export default PostLoginBillingModal;
